import { IonRouterOutlet, useIonRouter } from '@ionic/react';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route } from 'react-router';
import { useSentinelAccess, withRequiredPermission } from 'src/utils/PermissionGuard';
import { PdfViewerPage } from '../PdfViewerPage';
import StoneAgePage from '../StoneAgePage';
import { BundlesInfoPage } from '../bundles/BundlesInfoPage';
import BundlesListPage from '../bundles/BundlesListPage';
import { ControllerDevPage } from '../controller-dev/ControllerDevPage';
import { ControllerInfoPage } from '../controller-info-page/ControllerInfoPage';
import ControllersListPage from '../controllers/ControllersListPage';
import FirmwareListPage from '../firmware/FirewareListPage';
import { FirmwareInfoPage } from '../firmware/FirmwareInfoPage';
import HomePage from '../home/HomePage';
import MobileHomePage from '../home/MobileHomePage';
import { JobInfoByFilenamePage } from '../job-info/JobInfoByFilenamePage';
import { JobInfoByIdPage } from '../job-info/JobInfoByIdPage';
import LogsListPage from '../jobs/LogsListPage';
import MobileBundlesListPage from '../bundles/MobileBundlesListPage';
import MobileLogsListPage from '../jobs/MobileLogsListPage';
import { MobileControllersListPage } from '../mobile-controller-page/MobileControllersListPage';
import { MobileProfilePage } from '../profile/MobileProfilePage';
import { ProfilePage } from '../profile/ProfilePage';
import { StoneAgeUsersPage } from '../stoneage-users-page/StoneAgeUsersPage';
import { OrganizationSubscriptionsPage } from '../OrganizationSubscriptionsPage';
import { BundleInstructionsPage } from '../bundles/BundleInstructionsPage';
import { AppRouteConstants } from './AppRouteConstants';
import { useIsMobile } from 'src/utils/helpers/platformHelpers';
import { selectAuthUserInfo } from '../../redux/auth/selectors';

interface AppRoutesProps {}

const GuardedLogsListPage = withRequiredPermission('ViewJobLogs')(LogsListPage);
const GuardedMobileLogsListPage = withRequiredPermission('ViewJobLogs')(MobileLogsListPage);
const GuardedJobInfoByIdPage = withRequiredPermission('ViewJobLogs')(JobInfoByIdPage);
const GuardedJobInfoByFilenamePage = withRequiredPermission('ViewJobLogs')(JobInfoByFilenamePage);
const GuardedControllerPage = withRequiredPermission('ViewControllers')(MobileControllersListPage);
const GuardedControllersListPage = withRequiredPermission('ViewControllers')(ControllersListPage);
const GuardedControllerInfoPage = withRequiredPermission('ViewControllers')(ControllerInfoPage);
const GuardedFirmwareListPage = withRequiredPermission('ViewFirmware')(FirmwareListPage);
const GuardedFirmwareInfoPage = withRequiredPermission('ViewControllers')(FirmwareInfoPage);
const GuardedStoneAgeUsersPage = withRequiredPermission('ViewStoneAgeUsers')(StoneAgeUsersPage);
const GuardedBundleProfilesInfoByIdPage = withRequiredPermission('ViewBundles')(BundlesInfoPage);
const GuardedBundlesListPage = withRequiredPermission('ViewBundles')(BundlesListPage);
const GuardedMobileBundlesListPage = withRequiredPermission('ViewBundles')(MobileBundlesListPage);
const GuardedOrganizationSubscriptionPage = withRequiredPermission('ViewOrganizationSubscriptions')(
  OrganizationSubscriptionsPage,
);
const GuardedBundleInstructionsPage = withRequiredPermission('ViewBundles')(BundleInstructionsPage);

export const AppRoutes: React.FC<AppRoutesProps> = () => {
  const router = useIonRouter();
  const sentinelAccess = useSentinelAccess();
  const user = useSelector(selectAuthUserInfo);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (sentinelAccess === 'loading') return;
    if (!user?.tosAcceptedAt) {
      router.push('/tos', 'root', 'replace');
      return;
    }
    if (sentinelAccess !== 'granted') {
      router.push('/landing?accessDenied=true', 'root', 'replace');
      return;
    }
  }, [router, sentinelAccess, user?.tosAcceptedAt]);

  if (sentinelAccess !== 'granted') {
    return null;
  }

  return (
    <IonRouterOutlet>
      <Route exact path="/home" component={isMobile ? MobileHomePage : HomePage} />
      <Route
        exact
        path={AppRouteConstants.LogsList}
        component={isMobile ? GuardedMobileLogsListPage : GuardedLogsListPage}
      />
      <Route
        exact
        path={AppRouteConstants.BundlesList}
        component={isMobile ? GuardedMobileBundlesListPage : GuardedBundlesListPage}
      />
      <Route exact path={AppRouteConstants.BundleInstructions} component={GuardedBundleInstructionsPage} />
      <Route exact path="/jobs/file/:filename" component={GuardedJobInfoByFilenamePage} />

      <Route exact path={AppRouteConstants.LogDetails} component={GuardedJobInfoByIdPage} />
      <Route exact path={AppRouteConstants.BundleDetails} component={GuardedBundleProfilesInfoByIdPage} />

      <Route exact path="/controllers" component={isMobile ? GuardedControllerPage : GuardedControllersListPage} />
      <Route exact path="/controllers-dev" component={ControllerDevPage} />
      <Route exact path="/controllers/:id" component={GuardedControllerInfoPage} />
      <Route exact path="/dev-firmware" component={GuardedFirmwareListPage} />
      <Route exact path="/dev-firmware/:id" component={GuardedFirmwareInfoPage} />
      <Route exact path="/profile" component={isMobile ? MobileProfilePage : ProfilePage} />
      <Route exact path="/stoneage-users" component={GuardedStoneAgeUsersPage} />
      <Route exact path="/organization-subscriptions" component={GuardedOrganizationSubscriptionPage} />
      <Route path="/pdf-viewer/:file" component={PdfViewerPage} />
      <Route path="/stone-age" component={StoneAgePage} />

      <Redirect exact from="/bundles" to={AppRouteConstants.BundlesList} />
      <Redirect exact from="/jobs" to={AppRouteConstants.LogsList} />
      <Redirect exact from="/bundles/:id" to={AppRouteConstants.BundleDetails} />
      <Redirect exact from="/bundles-instructions" to={AppRouteConstants.BundleInstructions} />

      <Route exact path="/">
        <Redirect to="/home" />
      </Route>

      {/* amplify-config.ts contains configuration to send user to /welcome after sign in.  We'll redirect that to /home */}
      <Route exact path="/welcome">
        <Redirect to="/home" />
      </Route>
    </IonRouterOutlet>
  );
};
